import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1688592969417-953dd3c2b9d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
};

const galleryImages = {
  all: [
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/ouszjghc_Beard%20grooming.png", category: 'braids', title: 'Knotless Braids' },
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/4y9mb33z_Braids.png", category: 'braids', title: 'Goddess Braids' },
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/32ujcf34_Knotless%20braid.png", category: 'braids', title: 'Box Braids' },
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/yko94aez_Knotless%20braids.png", category: 'braids', title: 'Knotless Style' },
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/26a91t3s_Goddess%20Braid.png", category: 'braids', title: 'Cornrow Braids' },
    { src: "https://images.unsplash.com/photo-1632765866070-3fadf25d3d5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'natural', title: 'Natural Afro' },
    { src: "https://images.unsplash.com/photo-1615453261246-4b32e335a4a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'natural', title: 'Natural Volume' },
    { src: "https://images.unsplash.com/photo-1508204152195-1ce799200f89?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'natural', title: 'Natural Styling' },
    { src: "https://images.unsplash.com/photo-1612214070495-ffc6b69cda4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'men', title: 'Skin Fade' },
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/wu3ozl2f_Skin%20fade.png", category: 'men', title: 'Beard Grooming' },
    { src: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/6awovhei_Temp%20Fade%20and%20Sponged%20Hair.png", category: 'men', title: 'Temp Fade' },
    { src: "https://images.unsplash.com/photo-1768876035832-62f1206008ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'locs', title: 'Long Locs' },
    { src: "https://images.unsplash.com/photo-1625536658679-42d76fd167c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'locs', title: 'Styled Locs' },
    { src: "https://images.unsplash.com/photo-1625536658536-a94c4349850b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'locs', title: 'Locs with Glasses' },
    { src: "https://images.unsplash.com/photo-1663582816158-42354522fe15?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'wigs', title: 'Wig Installation' },
    { src: "https://images.unsplash.com/photo-1588527962980-72746d95973e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", category: 'wigs', title: 'Curly Wig' },
  ],
};

const categories = [
  { id: 'all', label: 'All Styles' },
  { id: 'braids', label: 'Braids' },
  { id: 'natural', label: 'Natural Hair' },
  { id: 'wigs', label: 'Wigs' },
  { id: 'men', label: "Men's Cuts" },
  { id: 'locs', label: 'Locs & Twists' },
];

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

export const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = activeCategory === 'all' 
    ? galleryImages.all 
    : galleryImages.all.filter(img => img.category === activeCategory);

  return (
    <main data-testid="gallery-page">
      {/* Page Header */}
      <section 
        className="page-header"
        style={{ 
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#0A0A0A]" />
        <div className="container-custom relative z-10 text-center pt-24">
          <motion.p 
            className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Style Inspiration
          </motion.p>
          <motion.h1 
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Our <span className="italic">Gallery</span>
          </motion.h1>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="gallery-content">
        <div className="container-custom">
          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                data-testid={`gallery-filter-${category.id}`}
                className={`tab-btn ${activeCategory === category.id ? 'active' : ''}`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            layout
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={`${image.src}-${index}`}
                  className={`img-container cursor-pointer overflow-hidden ${
                    index === 0 || index === 5 || index === 10 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                  style={{ aspectRatio: index === 0 || index === 5 || index === 10 ? '1' : '1' }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <img 
                    src={image.src} 
                    alt={image.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition-colors flex items-end p-4 opacity-0 hover:opacity-100">
                    <span className="text-white font-heading text-lg">{image.title}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-[#C9A227] transition-colors"
              onClick={() => setSelectedImage(null)}
              aria-label="Close lightbox"
            >
              <X size={32} />
            </button>
            <motion.div
              className="max-w-4xl max-h-[80vh] relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedImage.src} 
                alt={selectedImage.title}
                className="max-w-full max-h-[80vh] object-contain"
              />
              <p className="text-center text-white font-heading text-2xl mt-4">
                {selectedImage.title}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#C9A227]" data-testid="gallery-cta">
        <div className="container-custom">
          <motion.div 
            className="max-w-2xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#000] mb-6"
              variants={fadeInUp}
            >
              Inspired by What You <span className="italic">See</span>?
            </motion.h2>
            <motion.p 
              className="text-[#2E2E2E] text-lg mb-10"
              variants={fadeInUp}
            >
              Book an appointment today and let our talented team create your perfect look.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link 
                to="/book" 
                data-testid="gallery-book-btn" 
                className="btn-primary bg-[#000] text-[#F5F5F5] hover:bg-[#2E2E2E]"
              >
                Book Appointment
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Gallery;
